import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Trophy,
  Timer,
  User,
  Users,
  ChevronDown,
  BookOpen,
  RotateCcw,
  LogOut,
  Search,
  Eye,
  EyeOff,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import MatchResultDialog from "@/games/shared/MatchResultDialog";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { LiveAnnouncer, useAnnouncer } from "@/games/shared/LiveAnnouncer";
import GameMenu from "@/games/shared/GameMenu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  elements,
  categoryLabels,
  type Element,
  type ElementCategory,
} from "@/data/elements";
import {
  generateQuestion,
  scoreAnswer,
  SPRINT_FAST_THRESHOLD_S,
  SPRINT_POINTS_FAST,
  SPRINT_POINTS_SLOW,
  SPRINT_TURN_SECONDS,
  type SprintOutcome,
  type SprintQuestion,
} from "@/games/periodicSprint/engine";

const QUESTIONS_PER_MATCH = 12;
const RESULT_PAUSE_MS = 1700;
const SOLO_BEST_KEY = "elementpad:periodic-sprint:best";

type Mode = "solo" | "hot-seat";

type HistoryEntry = {
  id: string;
  name: string;
  symbol: string;
  category: ElementCategory;
  why: string;
  outcome: SprintOutcome;
};

const readBest = (): number => {
  try {
    const raw = window.localStorage.getItem(SOLO_BEST_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
};
const writeBest = (score: number) => {
  try {
    window.localStorage.setItem(SOLO_BEST_KEY, String(score));
  } catch {
    /* ignore */
  }
};

/**
 * Static class lookup so Tailwind can statically extract the bg-cat-*
 * classes (no dynamic string interpolation).
 */
const CATEGORY_BG: Record<ElementCategory, string> = {
  "alkali-metal": "bg-cat-alkali/30",
  "alkaline-earth": "bg-cat-alkaline/30",
  "transition-metal": "bg-cat-transition/30",
  "post-transition-metal": "bg-cat-post-transition/30",
  metalloid: "bg-cat-metalloid/30",
  nonmetal: "bg-cat-nonmetal/30",
  halogen: "bg-cat-halogen/30",
  "noble-gas": "bg-cat-noble/30",
  lanthanide: "bg-cat-lanthanide/30",
  actinide: "bg-cat-actinide/30",
  unknown: "bg-cat-unknown/30",
};

function whyForElement(el: Element): string {
  const groupNote =
    el.group != null ? `group ${el.group}` : "the f-block (lanthanides/actinides)";
  return `${el.name} (${el.symbol}) is element ${el.number} - period ${el.period}, ${groupNote}, ${categoryLabels[el.category].toLowerCase()}.`;
}

function sprintAnnouncement(outcome: SprintOutcome, target: Element, points: number): string {
  switch (outcome) {
    case "fast":
      return `Correct - ${target.name}, ${target.symbol}. Fast bonus, plus ${points} points.`;
    case "slow":
      return `Correct - ${target.name}, ${target.symbol}. Plus ${points} points.`;
    case "wrong":
      return `Incorrect. The answer was ${target.name}, ${target.symbol}.`;
    case "timeout":
      return `Time's up. The answer was ${target.name}, ${target.symbol}.`;
  }
}

/**
 * Periodic Sprint - locate the named element on the periodic table.
 *
 * Solo and Hot-seat 2P modes share the same question bank (all 118
 * elements). The grid is intentionally compact and unlabeled by default
 * so it tests recall, not reading; an "show symbols" toggle helps newer
 * players ease in without changing scoring.
 */
export default function PeriodicSprint() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode | null>(null);

  return (
    <>
      <LiveAnnouncer />
      {!mode ? (
        <ModeSelect onPick={setMode} />
      ) : mode === "solo" ? (
        <SoloMatch
          onChangeMode={() => setMode(null)}
          onExit={() => navigate("/play")}
        />
      ) : (
        <HotSeatMatch
          onChangeMode={() => setMode(null)}
          onExit={() => navigate("/play")}
        />
      )}
    </>
  );
}

// ---------------- mode select ----------------

function ModeSelect({ onPick }: { onPick: (m: Mode) => void }) {
  return (
    <GameLayout
      title="Periodic Sprint"
      subtitle="Find the named element on the periodic table - race the clock."
      headerActions={<GameMenu gameKey="periodic-sprint" />}
    >
      <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Search size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Choose a mode</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Solo for personal best, or hot-seat to compete head-to-head.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <ModeButton
            icon={<User size={14} className="text-primary" aria-hidden="true" />}
            label="Solo"
            blurb="One player. Best score saved."
            onClick={() => onPick("solo")}
          />
          <ModeButton
            icon={<Users size={14} className="text-primary" aria-hidden="true" />}
            label="Hot-seat 2P"
            blurb="Alternate questions. Highest total wins."
            onClick={() => onPick("hot-seat")}
          />
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          {SPRINT_TURN_SECONDS}s per question · ≤{SPRINT_FAST_THRESHOLD_S}s ={" "}
          {SPRINT_POINTS_FAST} pts · slower correct = {SPRINT_POINTS_SLOW} pts.
        </p>
      </div>
    </GameLayout>
  );
}

function ModeButton({
  icon,
  label,
  blurb,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  blurb: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-semibold text-foreground">{label}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-snug">{blurb}</p>
    </button>
  );
}

// ---------------- solo ----------------

type SoloPhase =
  | { kind: "playing"; questionIndex: number; question: SprintQuestion; startedAt: number; score: number }
  | {
      kind: "result";
      questionIndex: number;
      question: SprintQuestion;
      chosenNumber: number | null;
      score: number;
      lastAward: number;
      outcome: SprintOutcome;
    }
  | { kind: "finished"; score: number; best: number };

function makeFirstQuestion(score: number): SoloPhase {
  const q = generateQuestion(Math.random, new Set());
  return {
    kind: "playing",
    questionIndex: 0,
    question: q,
    startedAt: performance.now(),
    score,
  };
}

function SoloMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const [phase, setPhase] = useState<SoloPhase>(() => makeFirstQuestion(0));
  const [showSymbols, setShowSymbols] = useState(false);
  const askedRef = useRef<Set<string>>(new Set());
  const historyRef = useRef<HistoryEntry[]>([]);
  const advanceTimer = useRef<number | null>(null);
  const announce = useAnnouncer();

  useEffect(() => {
    if (phase.kind === "playing") askedRef.current.add(phase.question.id);
  }, [phase]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const handleAnswer = useCallback(
    (chosenNumber: number | null) => {
      if (phase.kind !== "playing") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosenNumber, phase.question, elapsed);
      const nextScore = phase.score + points;
      const t = phase.question.target;
      historyRef.current.push({
        id: phase.question.id,
        name: t.name,
        symbol: t.symbol,
        category: t.category,
        why: whyForElement(t),
        outcome,
      });
      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");
      announce(
        sprintAnnouncement(outcome, t, points),
        outcome === "timeout" ? "assertive" : "polite",
      );

      setPhase({
        kind: "result",
        questionIndex: phase.questionIndex,
        question: phase.question,
        chosenNumber,
        score: nextScore,
        lastAward: points,
        outcome,
      });

      advanceTimer.current = window.setTimeout(() => {
        const nextIndex = phase.questionIndex + 1;
        if (nextIndex >= QUESTIONS_PER_MATCH) {
          const best = readBest();
          if (nextScore > best) writeBest(nextScore);
          play(nextScore > best ? "win" : "click");
          setPhase({ kind: "finished", score: nextScore, best: Math.max(best, nextScore) });
          return;
        }
        const nextQ = generateQuestion(Math.random, askedRef.current);
        askedRef.current.add(nextQ.id);
        setPhase({
          kind: "playing",
          questionIndex: nextIndex,
          question: nextQ,
          startedAt: performance.now(),
          score: nextScore,
        });
      }, RESULT_PAUSE_MS);
    },
    [phase],
  );

  const onExpire = useCallback(() => handleAnswer(null), [handleAnswer]);

  const restart = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    setPhase(makeFirstQuestion(0));
  };

  if (phase.kind === "finished") {
    return (
      <GameLayout
        title="Periodic Sprint"
        subtitle="Solo · 12 questions"
        headerActions={<GameMenu gameKey="periodic-sprint" onReset={restart} resetLabel="Restart" />}
      >
        <MatchRecap
          history={historyRef.current}
          stat={
            <div className="text-center">
              <Trophy size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Final score</p>
              <p className="text-4xl font-bold font-mono text-foreground tabular-nums mb-1">
                {phase.score}
              </p>
              <p className="text-xs text-muted-foreground">
                Personal best: <span className="font-mono">{phase.best}</span>
              </p>
            </div>
          }
          onExit={onExit}
          onChangeMode={onChangeMode}
          onAgain={restart}
          againLabel="Play again"
        />
      </GameLayout>
    );
  }

  const active = phase.kind === "playing" ? phase : null;
  const result = phase.kind === "result" ? phase : null;
  const q = active?.question ?? result!.question;
  const idx = active?.questionIndex ?? result!.questionIndex;
  const score = active?.score ?? result!.score;

  return (
    <GameLayout
      title="Periodic Sprint"
      subtitle="Solo · 12 questions"
      headerActions={<GameMenu gameKey="periodic-sprint" onReset={restart} resetLabel="Restart" />}
    >
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Question" value={`${idx + 1} / ${QUESTIONS_PER_MATCH}`} />
        <Stat label="Score" value={String(score)} mono />
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Time</span>
          {active ? (
            <TurnTimer
              key={`solo-${q.id}-${idx}`}
              seconds={SPRINT_TURN_SECONDS}
              onExpire={onExpire}
              size={40}
            />
          ) : (
            <Timer size={18} className="text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </div>

      <PromptCard
        question={q}
        showSymbols={showSymbols}
        onToggleSymbols={() => setShowSymbols((s) => !s)}
      />

      <PeriodicGrid
        target={q.target}
        chosenNumber={result?.chosenNumber ?? null}
        onChoose={(n) => active && handleAnswer(n)}
        revealed={!!result}
        showSymbols={showSymbols}
      />

      {result && (
        <ResultBanner
          outcome={result.outcome}
          award={result.lastAward}
          target={q.target}
        />
      )}
    </GameLayout>
  );
}

// ---------------- hot-seat ----------------

type HotSeatPhase =
  | { kind: "answering"; question: SprintQuestion; startedAt: number }
  | {
      kind: "result";
      question: SprintQuestion;
      chosenNumber: number | null;
      lastAward: number;
      outcome: SprintOutcome;
    }
  | { kind: "handoff"; nextPlayer: PlayerId };

function HotSeatMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const match = useHotSeatMatch(0);
  const [phase, setPhase] = useState<HotSeatPhase | null>(null);
  const [showSymbols, setShowSymbols] = useState(false);
  const askedRef = useRef<Set<string>>(new Set());
  const historyRef = useRef<HistoryEntry[]>([]);
  const advanceTimer = useRef<number | null>(null);
  const announce = useAnnouncer();
  const TOTAL_TURNS = QUESTIONS_PER_MATCH * 2;

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      askedRef.current = new Set();
      historyRef.current = [];
      match.start(p1, p2);
      setPhase({ kind: "handoff", nextPlayer: 0 });
    },
    [match],
  );

  const beginTurn = useCallback(() => {
    const q = generateQuestion(Math.random, askedRef.current);
    askedRef.current.add(q.id);
    setPhase({ kind: "answering", question: q, startedAt: performance.now() });
  }, []);

  const submitAnswer = useCallback(
    (chosenNumber: number | null) => {
      if (!phase || phase.kind !== "answering") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosenNumber, phase.question, elapsed);

      const current = match.state.currentTurn;
      const newScore = match.state.stats[current] + points;
      match.setStat(current, newScore);

      const t = phase.question.target;
      historyRef.current.push({
        id: phase.question.id,
        name: t.name,
        symbol: t.symbol,
        category: t.category,
        why: whyForElement(t),
        outcome,
      });

      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");
      announce(
        sprintAnnouncement(outcome, t, points),
        outcome === "timeout" ? "assertive" : "polite",
      );

      setPhase({ kind: "result", question: phase.question, chosenNumber, lastAward: points, outcome });

      advanceTimer.current = window.setTimeout(() => {
        if (match.state.turnNumber >= TOTAL_TURNS) {
          const a = match.state.stats[0] + (current === 0 ? points : 0);
          const b = match.state.stats[1] + (current === 1 ? points : 0);
          const winner: PlayerId | null = a === b ? null : a > b ? 0 : 1;
          play(winner === null ? "click" : "win");
          match.finish(winner);
          return;
        }
        match.nextTurn();
        const next: PlayerId = current === 0 ? 1 : 0;
        setPhase({ kind: "handoff", nextPlayer: next });
      }, RESULT_PAUSE_MS);
    },
    [phase, match, TOTAL_TURNS],
  );

  const onExpire = useCallback(() => submitAnswer(null), [submitAnswer]);

  const handleRematch = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.start(match.state.players[0].name, match.state.players[1].name);
    setPhase({ kind: "handoff", nextPlayer: 0 });
  };
  const handleChangePlayers = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.reset();
    setPhase(null);
  };

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Periodic Sprint"
        subtitle={`Hot-seat · ${QUESTIONS_PER_MATCH} questions each`}
        headerActions={<GameMenu gameKey="periodic-sprint" />}
      >
        <PlayerSetup
          title="Set up the match"
          description="Two players alternate questions on the same device. Highest total wins."
          ctaLabel="Start"
          onStart={startMatch}
        />
        <div className="max-w-md mx-auto mt-3 text-center">
          <button
            type="button"
            onClick={onChangeMode}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Change mode
          </button>
        </div>
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";
  const current = match.state.players[match.state.currentTurn];
  const turnsRemaining = Math.max(0, TOTAL_TURNS - match.state.turnNumber + 1);

  return (
    <GameLayout
      title="Periodic Sprint"
      subtitle="Hot-seat 2P"
      headerActions={<GameMenu gameKey="periodic-sprint" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Score" />
      </div>

      {phase?.kind === "handoff" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">Pass the device to:</p>
          <p className="text-2xl font-bold text-foreground mb-1">{current.name}</p>
          <p className="text-[11px] text-muted-foreground mb-4">
            Turn {match.state.turnNumber} of {TOTAL_TURNS} · {turnsRemaining} left
          </p>
          <Button onClick={beginTurn} size="lg">
            <Search size={14} className="mr-1.5" aria-hidden="true" />
            I'm ready
          </Button>
        </div>
      )}

      {phase?.kind === "answering" && !matchOver && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground">{current.name}'s turn</span>
            <TurnTimer
              key={`hs-${phase.question.id}-${match.state.turnNumber}`}
              seconds={SPRINT_TURN_SECONDS}
              onExpire={onExpire}
              size={44}
            />
          </div>
          <PromptCard
            question={phase.question}
            showSymbols={showSymbols}
            onToggleSymbols={() => setShowSymbols((s) => !s)}
          />
          <PeriodicGrid
            target={phase.question.target}
            chosenNumber={null}
            onChoose={submitAnswer}
            revealed={false}
            showSymbols={showSymbols}
          />
        </>
      )}

      {phase?.kind === "result" && !matchOver && (
        <>
          <PromptCard
            question={phase.question}
            showSymbols={showSymbols}
            onToggleSymbols={() => setShowSymbols((s) => !s)}
          />
          <PeriodicGrid
            target={phase.question.target}
            chosenNumber={phase.chosenNumber}
            onChoose={() => undefined}
            revealed
            showSymbols={showSymbols}
          />
          <ResultBanner
            outcome={phase.outcome}
            award={phase.lastAward}
            target={phase.question.target}
          />
        </>
      )}

      <MatchResultDialog
        state={match.state}
        open={matchOver}
        onRematch={handleRematch}
        onChangePlayers={handleChangePlayers}
        onExit={onExit}
        detail={`${QUESTIONS_PER_MATCH} questions each. Higher total wins.`}
      />
    </GameLayout>
  );
}

// ---------------- shared sub-components ----------------

function Stat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </div>
      <div
        className={`text-base font-semibold text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function PromptCard({
  question,
  showSymbols,
  onToggleSymbols,
}: {
  question: SprintQuestion;
  showSymbols: boolean;
  onToggleSymbols: () => void;
}) {
  return (
    <div className="rounded-xl border border-primary/40 bg-card p-4 mb-3 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
          Find this element
        </div>
        <h2 className="text-xl font-semibold text-foreground">{question.target.name}</h2>
      </div>
      <button
        type="button"
        onClick={onToggleSymbols}
        className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors text-muted-foreground"
        aria-pressed={showSymbols}
      >
        {showSymbols ? (
          <EyeOff size={12} aria-hidden="true" />
        ) : (
          <Eye size={12} aria-hidden="true" />
        )}
        {showSymbols ? "Hide symbols" : "Show symbols"}
      </button>
    </div>
  );
}

/**
 * Compact 18-column periodic table grid where each cell is a small
 * button. Lanthanides + actinides render on rows 9 and 10 with a small
 * top gap, mirroring the standard layout.
 *
 * Keyboard navigation: Arrow keys move focus across populated cells
 * (skipping empty positions and jumping into/out of the f-block rows
 * 9–10), Home/End jump to row ends, PageUp/PageDown jump rows,
 * Enter/Space selects. Each cell exposes a full screen-reader label
 * including name, symbol, atomic number, period, group, and category.
 */
function PeriodicGrid({
  target,
  chosenNumber,
  onChoose,
  revealed,
  showSymbols,
}: {
  target: Element;
  chosenNumber: number | null;
  onChoose: (n: number) => void;
  revealed: boolean;
  showSymbols: boolean;
}) {
  // Group elements by row for layout.
  const grid = useMemo(() => {
    const map = new Map<number, Element[]>();
    for (const el of elements) {
      const arr = map.get(el.row) ?? [];
      arr.push(el);
      map.set(el.row, arr);
    }
    return map;
  }, []);

  // Position lookup: "row,col" -> Element. Used for arrow-key nav so we
  // can skip empty grid cells (e.g. between H and He, the f-block gap).
  const byPos = useMemo(() => {
    const m = new Map<string, Element>();
    for (const el of elements) m.set(`${el.row},${el.col}`, el);
    return m;
  }, []);
  const ROWS = [1, 2, 3, 4, 5, 6, 7, 9, 10];
  const containerRef = useRef<HTMLDivElement | null>(null);

  const focusElement = useCallback((number: number) => {
    const root = containerRef.current;
    if (!root) return;
    const btn = root.querySelector<HTMLButtonElement>(
      `button[data-element-number="${number}"]`,
    );
    if (btn) btn.focus();
  }, []);

  const moveFocus = useCallback(
    (from: Element, dx: number, dy: number) => {
      // Walk in the requested direction until we find a populated cell
      // or fall off the grid. dy moves through ROWS array indexes so
      // the f-block rows feel adjacent to row 7.
      let rowIdx = ROWS.indexOf(from.row);
      let col = from.col;
      for (let step = 0; step < 30; step++) {
        rowIdx += dy;
        col += dx;
        if (rowIdx < 0 || rowIdx >= ROWS.length) return;
        if (col < 1 || col > 18) return;
        const cand = byPos.get(`${ROWS[rowIdx]},${col}`);
        if (cand) {
          focusElement(cand.number);
          return;
        }
        // Vertical: keep walking same column. Horizontal: keep walking row.
        if (dx !== 0) rowIdx -= dy;
        if (dy !== 0) col -= dx;
      }
    },
    [byPos, focusElement],
  );

  const onCellKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, el: Element) => {
      switch (e.key) {
        case "Enter":
        case " ":
        case "Spacebar":
          e.preventDefault();
          if (!revealed) onChoose(el.number);
          return;
        case "ArrowRight":
          e.preventDefault();
          moveFocus(el, 1, 0);
          return;
        case "ArrowLeft":
          e.preventDefault();
          moveFocus(el, -1, 0);
          return;
        case "ArrowDown":
          e.preventDefault();
          moveFocus(el, 0, 1);
          return;
        case "ArrowUp":
          e.preventDefault();
          moveFocus(el, 0, -1);
          return;
        case "Home": {
          e.preventDefault();
          // First populated cell in the row.
          const row = grid.get(el.row) ?? [];
          if (row[0]) focusElement(row[0].number);
          return;
        }
        case "End": {
          e.preventDefault();
          const row = grid.get(el.row) ?? [];
          if (row.length) focusElement(row[row.length - 1].number);
          return;
        }
        default:
          return;
      }
    },
    [grid, moveFocus, focusElement, revealed, onChoose],
  );

  const renderRow = (row: number) => {
    const els = grid.get(row) ?? [];
    return els.map((el) => {
      const isTarget = el.number === target.number;
      const isChosen = el.number === chosenNumber;
      const colorVar = CATEGORY_BG[el.category];
      const stateClasses = revealed
        ? isTarget
          ? "border-primary bg-primary text-primary-foreground"
          : isChosen
            ? "border-destructive/70 bg-destructive/20 text-foreground"
            : `border-border ${colorVar} text-muted-foreground opacity-70`
        : `border-border ${colorVar} hover:border-primary hover:bg-primary/20 text-foreground`;
      // Roving tabindex: only the first cell (H) is in the tab order
      // until the user moves focus into the grid; arrow keys move
      // focus between cells.
      const isFirstCell = el.number === 1;
      const groupLabel =
        el.group != null ? `group ${el.group}` : "f-block";
      const stateLabel = revealed
        ? isTarget
          ? ", correct answer"
          : isChosen
            ? ", your answer (incorrect)"
            : ""
        : "";
      const ariaLabel =
        `${el.name}, symbol ${el.symbol}, atomic number ${el.number}, ` +
        `period ${el.period}, ${groupLabel}, ${categoryLabels[el.category]}` +
        stateLabel;
      return (
        <button
          key={el.number}
          type="button"
          onClick={() => !revealed && onChoose(el.number)}
          onKeyDown={(e) => onCellKeyDown(e, el)}
          disabled={revealed}
          aria-label={ariaLabel}
          aria-pressed={isChosen}
          data-element-number={el.number}
          tabIndex={isFirstCell ? 0 : -1}
          role="gridcell"
          style={{ gridColumn: el.col, gridRow: 1 }}
          className={`aspect-square text-[9px] sm:text-[10px] font-mono leading-none flex items-center justify-center rounded-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default ${stateClasses}`}
          title={revealed ? el.name : undefined}
        >
          {showSymbols || (revealed && (isTarget || isChosen)) ? el.symbol : ""}
        </button>
      );
    });
  };

  return (
    <div
      ref={containerRef}
      className="rounded-xl border border-border bg-card p-3 mb-3 overflow-x-auto"
      role="grid"
      aria-label="Periodic table - use arrow keys to move, Enter to select"
      aria-rowcount={9}
      aria-colcount={18}
    >
      <div className="min-w-[640px]">
        {/* Main 7 periods */}
        <div className="space-y-0.5">
          {[1, 2, 3, 4, 5, 6, 7].map((row) => (
            <div
              key={row}
              role="row"
              className="grid gap-0.5"
              style={{ gridTemplateColumns: "repeat(18, minmax(0, 1fr))" }}
            >
              {renderRow(row)}
            </div>
          ))}
        </div>
        {/* Lanthanides + actinides */}
        <div className="mt-2 space-y-0.5">
          {[9, 10].map((row) => (
            <div
              key={row}
              role="row"
              className="grid gap-0.5"
              style={{ gridTemplateColumns: "repeat(18, minmax(0, 1fr))" }}
            >
              {renderRow(row)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResultBanner({
  outcome,
  award,
  target,
}: {
  outcome: SprintOutcome;
  award: number;
  target: Element;
}) {
  const positive = outcome === "fast" || outcome === "slow";
  const why = whyForElement(target);
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border p-3 ${
        positive ? "border-primary/40 bg-primary/5" : "border-destructive/30 bg-destructive/5"
      }`}
    >
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {positive ? (
          <CheckCircle2 size={14} className="text-primary" aria-hidden="true" />
        ) : (
          <XCircle size={14} className="text-destructive" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {outcome === "fast" && `Fast & correct! +${award} pts`}
          {outcome === "slow" && `Correct. +${award} pts`}
          {outcome === "wrong" && `Not quite - that wasn't ${target.name}.`}
          {outcome === "timeout" && `Time's up - ${target.name} (${target.symbol}).`}
        </span>
        <Badge variant="secondary" className="text-[10px] ml-auto font-mono">
          #{target.number} {target.symbol}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{why}</p>
    </div>
  );
}

// ---------------- recap ----------------

function MatchRecap({
  stat,
  history,
  onExit,
  onChangeMode,
  onAgain,
  againLabel,
}: {
  stat: React.ReactNode;
  history: HistoryEntry[];
  onExit: () => void;
  onChangeMode: () => void;
  onAgain: () => void;
  againLabel: string;
}) {
  const total = history.length;
  const correct = history.filter((h) => h.outcome === "fast" || h.outcome === "slow").length;
  const wrong = history.filter((h) => h.outcome === "wrong").length;
  const timeout = history.filter((h) => h.outcome === "timeout").length;
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

  const byCategory = useMemo(() => {
    const map = new Map<ElementCategory, { c: number; t: number }>();
    for (const h of history) {
      const cur = map.get(h.category) ?? { c: 0, t: 0 };
      cur.t += 1;
      if (h.outcome === "fast" || h.outcome === "slow") cur.c += 1;
      map.set(h.category, cur);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].t - a[1].t);
  }, [history]);

  const topLessons = useMemo(() => {
    const seen = new Set<string>();
    const lessons: HistoryEntry[] = [];
    for (const h of history) {
      if (h.outcome === "wrong" || h.outcome === "timeout") {
        if (seen.has(h.id)) continue;
        seen.add(h.id);
        lessons.push(h);
        if (lessons.length >= 3) return lessons;
      }
    }
    for (const h of history) {
      if (lessons.length >= 3) break;
      if (seen.has(h.id)) continue;
      seen.add(h.id);
      lessons.push(h);
    }
    return lessons;
  }, [history]);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="rounded-xl border border-border bg-card p-6">{stat}</div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">Performance</h3>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <BreakdownStat label="Correct" value={correct} tone="positive" />
          <BreakdownStat label="Wrong" value={wrong} tone="negative" />
          <BreakdownStat label="Timeout" value={timeout} tone="muted" />
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Accuracy
            </span>
            <span className="text-sm font-mono tabular-nums text-foreground">{accuracy}%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${accuracy}%` }} />
          </div>
        </div>

        {byCategory.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {byCategory.map(([cat, { c, t }]) => (
              <div
                key={cat}
                className="flex items-center justify-between text-xs px-3 py-2 rounded-md border border-border bg-background"
              >
                <span className="text-muted-foreground">{categoryLabels[cat]}</span>
                <span className="font-mono tabular-nums text-foreground">
                  {c}/{t}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {topLessons.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={14} className="text-primary" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-foreground">Top lessons</h3>
          </div>
          <ul className="space-y-2">
            {topLessons.map((l) => (
              <li key={l.id} className="rounded-md border border-border bg-background p-3">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold text-foreground">
                    {l.name} <span className="text-muted-foreground font-mono">({l.symbol})</span>
                  </span>
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                    {categoryLabels[l.category]}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{l.why}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button variant="outline" onClick={onExit}>
          <LogOut size={14} className="mr-1.5" aria-hidden="true" />
          Quit
        </Button>
        <Button variant="outline" onClick={onChangeMode}>
          Change mode
        </Button>
        <Button onClick={onAgain}>
          <RotateCcw size={14} className="mr-1.5" aria-hidden="true" />
          {againLabel}
        </Button>
      </div>
    </div>
  );
}

function BreakdownStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "positive" | "negative" | "muted";
}) {
  const toneClass =
    tone === "positive"
      ? "text-primary"
      : tone === "negative"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className="rounded-md border border-border bg-background p-3 text-center">
      <div className={`text-2xl font-bold font-mono tabular-nums ${toneClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
        {label}
      </div>
    </div>
  );
}